import { act } from "../../jsx-act";
import SpaceBetween from "../utilities/space-between";
import UXDefinitions  from "../../definitions/ux-definitions";
import Button from "../utilities/button";
import Modal from "../utilities/modal";
import ColumnLayout from "../utilities/column-layout";
import FormField from "../utilities/form-field";
import { RADIO_GROUP_INPUT_NAME_PREFIX } from "../utilities/radio-group";
import RadioGroup from "../utilities/radio-group";
import { CCBA_MODAL_ID } from "../../constants/ux-constants";
import { getConsentCookie as getConsentCookieCore } from "../../cookie";
import "./ccba-selector.scss";
import { COOKIE_CHANGE_EVENT, DEFAULT_COOKIE } from "../../constants/cookie-constants";

interface CcbaSelectorProps {
  content: UXDefinitions.CcbaSelectorSectionLocalization;
  isRtl: boolean;
  localizedLabelText?: string;
  darkModeEnabled?: boolean;
  runtimeEnvironment?: UXDefinitions.RuntimeEnvironment; // not needed since the component will be the same
  getConsentCookie: () => ReturnType<typeof getConsentCookieCore>
  handleSaveClick: UXDefinitions.HandleSaveClick;
  handleCancelClick: UXDefinitions.HandleCancelClick;
  gpcEnabled?: boolean;
}

let cookieChangeHandler: ((event: any) => void) | null = null;

export const CcbaSelector = ({
  isRtl,
  getConsentCookie,
  handleSaveClick,
  handleCancelClick,
  content,
  darkModeEnabled = false,
  gpcEnabled = false,
}: CcbaSelectorProps) => {
  const CCBA_SELECTOR_RADIO_BUTTON_NAME_SUFFIX = 'ccba-allowed';
  const  CCBA_SELECTOR_RADIO_BUTTON_NAME =`${RADIO_GROUP_INPUT_NAME_PREFIX}${CCBA_SELECTOR_RADIO_BUTTON_NAME_SUFFIX}`;
  const CCBA_SELECTOR_VALUE_ALLOWED = 'allowed';
  const CCBA_SELECTOR_VALUE_NOT_ALLOWED = 'not allowed';

  const getCookie = () => ({ ...(getConsentCookie() || DEFAULT_COOKIE) });

  const getCcbaSelectionFromCookie = () => {
    const cookie = getCookie();
    return cookie["ccba"];
  };

  const updateCcbaSelection = (
    ccbaSelected: string
  ) => {
    const cookie = getCookie()
    cookie["ccba"] = ccbaSelected === "allowed" ? true : false;
    handleSaveClick(cookie, 'ccbaModal');
  };

  /**
   * Listens for cookie change events and updates the radio buttons accordingly
   */
  if (cookieChangeHandler) {
    document.removeEventListener(COOKIE_CHANGE_EVENT, cookieChangeHandler);
  }
  cookieChangeHandler = (event: any) => {
    const ccbaSelected = event.detail.cookie.ccba
    const allowedRadio = document.querySelector(`input[name='${CCBA_SELECTOR_RADIO_BUTTON_NAME}'][value='${CCBA_SELECTOR_VALUE_ALLOWED}']`) as HTMLInputElement | null;
    const notAllowedRadio = document.querySelector(`input[name='${CCBA_SELECTOR_RADIO_BUTTON_NAME}'][value='${CCBA_SELECTOR_VALUE_NOT_ALLOWED}']`) as HTMLInputElement | null;

    if (allowedRadio && notAllowedRadio) {
      if (ccbaSelected === true) {
        allowedRadio.checked = true;
        notAllowedRadio.checked = false;
      } else {
        allowedRadio.checked = false;
        notAllowedRadio.checked = true;
      }
    }
  };
  document.addEventListener(COOKIE_CHANGE_EVENT, cookieChangeHandler);

  const initializeCcbaSelection = () => {
    // If GPC is enabled, force "not allowed"
    if (gpcEnabled) {
      return false;
    }
    
    const allowCcba = getCcbaSelectionFromCookie();
    let ccbaPropertyValue = true;
    if (allowCcba === false) {
      ccbaPropertyValue = false;
    }
    return ccbaPropertyValue;
  };

  let value = initializeCcbaSelection() === true ? "allowed" : "not allowed";

  const handleSaveButtonClick = () => {
    updateCcbaSelection(value);
  };

  return (
    <Modal
      isRTL={isRtl}
      id={CCBA_MODAL_ID}
      size="large"
      onDismiss={handleCancelClick}
      darkModeEnabled={darkModeEnabled}
      footer={
        <div id="ccba-footer" data-testid="ccba-footer">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="cancel-button"
              variant="secondary"
              onClick={() => {
                handleCancelClick('ccbaModal');
              }}
            >
              {content["cancel-button"]}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveButtonClick}
            >
              {content["save-button"]}
            </Button>
          </SpaceBetween>
        </div>
      }
      header={<span id="ccba-header">{content["tittle"]}</span>}
    >
      {gpcEnabled && content["gpc-warning-title"] ? (
        <div class="awsccc-gpc-warning" role="alert">
          <strong>{content["gpc-warning-title"]}</strong>
          <p>{content["gpc-warning-message"]}</p>
        </div>
      ) : null}
      <ColumnLayout columns={1} borders="horizontal" data-testid="ccba-content" class="awsccc-cbbas-c">
        <SpaceBetween size="m" direction="vertical">
          <p>{content["introduction"]}</p>
          <p>{content["optout-intro"]}</p>
        </SpaceBetween>
        <FormField label={""}>
          <RadioGroup
            name={CCBA_SELECTOR_RADIO_BUTTON_NAME_SUFFIX}
            ariaLabel="Cross-context behavioral ads"
            onChange={({ detail }) => {
              if (!gpcEnabled) {
                value = detail.value;
              }
            }}
            initialValue={value}
            items={[
              { value: CCBA_SELECTOR_VALUE_ALLOWED, label: content["option1"], disabled: gpcEnabled },
              { value: CCBA_SELECTOR_VALUE_NOT_ALLOWED, label: content["option2"], disabled: gpcEnabled },
            ]}
          />
        </FormField>
        <SpaceBetween size="s" direction="vertical">
          <p>{content["extra-info-p1"]}</p>
          <p>{content["extra-info-p2"]}</p>
        </SpaceBetween>
      </ColumnLayout>
    </Modal>
  );
};
